import { useEffect, useState } from 'react';
import { Send, UserCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Ticket {
  id: string;
  subject: string;
  priority: string;
  status: string;
  assigned_to_user_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
}
interface Message {
  id: string;
  sender_user_id: string | null;
  body: string;
  is_internal: boolean;
  created_at: string;
}

const STATUSES = ['open', 'pending', 'resolved', 'closed'] as const;

export function SupportPage() {
  const currentUser = useAuth((s) => s.user);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('open');
  const [list, setList] = useState<Ticket[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<{ ticket: Ticket; messages: Message[] } | null>(null);
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    api<{ data: Ticket[] }>(`/admin/support-tickets?status=${status}`)
      .then((r) => setList(r.data))
      .catch(() => setList([]));
  }
  useEffect(load, [status]);

  useEffect(() => {
    if (selected === null) {
      setThread(null);
      return;
    }
    api<{ data: { ticket: Ticket; messages: Message[] } }>(`/admin/support-tickets/${selected}`)
      .then((r) => setThread(r.data))
      .catch(() => setThread(null));
  }, [selected]);

  async function send() {
    if (selected === null || draft.trim().length === 0) return;
    try {
      await api(`/admin/support-tickets/${selected}/messages`, {
        method: 'POST',
        json: { body: draft, internal_note: internal },
      });
      setDraft('');
      // refetch thread
      const r = await api<{ data: { ticket: Ticket; messages: Message[] } }>(
        `/admin/support-tickets/${selected}`,
      );
      setThread(r.data);
    } catch {
      setError('Send failed.');
    }
  }

  async function refreshThread() {
    if (selected === null) return;
    const r = await api<{ data: { ticket: Ticket; messages: Message[] } }>(
      `/admin/support-tickets/${selected}`,
    );
    setThread(r.data);
  }

  async function setTicketStatus(next: string) {
    if (selected === null) return;
    try {
      await api(`/admin/support-tickets/${selected}/status`, {
        method: 'POST',
        json: { status: next },
      });
      load();
      await refreshThread();
    } catch {
      setError('Status change failed.');
    }
  }

  async function assignToMe() {
    if (selected === null || currentUser === null) return;
    try {
      await api(`/admin/support-tickets/${selected}/assign`, {
        method: 'POST',
        json: { user_id: currentUser.id },
      });
      load();
      await refreshThread();
    } catch {
      setError('Assignment failed.');
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Support tickets</h1>
        <p className="text-sm text-muted-foreground">Triage, assign, reply, and change status.</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setStatus(s);
              setSelected(null);
            }}
          >
            {s}
          </Button>
        ))}
      </div>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
        <Card>
          <CardHeader>
            <CardTitle>{list?.length ?? 0} tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {list === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
            {list?.length === 0 && (
              <p className="text-sm text-muted-foreground">Nothing in this state.</p>
            )}
            {list !== null && list.length > 0 && (
              <ul className="space-y-2">
                {list.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(t.id)}
                      className={`w-full rounded-2xl border p-3 text-left transition ${selected === t.id ? 'border-foreground bg-muted' : 'border-border hover:bg-muted'}`}
                    >
                      <p className="truncate font-bold">{t.subject}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="secondary">{t.priority}</Badge>
                        <Badge variant="secondary">{t.status}</Badge>
                        <span>· {formatDate(t.created_at)}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Conversation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {thread === null && (
              <p className="text-sm text-muted-foreground">Pick a ticket on the left.</p>
            )}
            {thread !== null && (
              <>
                <header className="flex flex-wrap items-center gap-2">
                  <p className="font-bold">{thread.ticket.subject}</p>
                  <Badge>{thread.ticket.status}</Badge>
                  {thread.ticket.assigned_to_user_id === currentUser?.id &&
                    currentUser !== null && <Badge variant="secondary">Assigned to you</Badge>}
                  <div className="ml-auto flex items-center gap-2">
                    {thread.ticket.assigned_to_user_id !== currentUser?.id && (
                      <Button size="sm" variant="outline" onClick={() => void assignToMe()}>
                        <UserCheck className="mr-1 h-4 w-4" /> Assign to me
                      </Button>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      Status
                      <select
                        aria-label="Ticket status"
                        value={thread.ticket.status}
                        onChange={(e) => void setTicketStatus(e.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </header>
                <ul className="space-y-2 text-sm">
                  {thread.messages.map((m) => (
                    <li
                      key={m.id}
                      className={`rounded-2xl border p-3 ${m.is_internal ? 'border-yellow-300 bg-yellow-50' : 'border-border'}`}
                    >
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDateTime(m.created_at)}
                        {m.is_internal && ' · internal note'}
                      </p>
                    </li>
                  ))}
                  {thread.messages.length === 0 && (
                    <p className="text-sm text-muted-foreground">No messages yet.</p>
                  )}
                </ul>
                <form
                  className="space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send();
                  }}
                >
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    placeholder="Type a reply…"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={internal}
                        onChange={(e) => setInternal(e.target.checked)}
                      />
                      Internal note
                    </label>
                    <Button type="submit" disabled={draft.trim().length === 0}>
                      <Send className="mr-2 h-4 w-4" /> Send
                    </Button>
                  </div>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short' }).format(new Date(iso));
  } catch {
    return iso;
  }
}
function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}
